/**
 * Copyright (c) 2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { Column, Table } from '../../mol-data/db';
import { Task } from '../../mol-task';
import { createModels } from './basic/parser';
import { BasicSchema, createBasic } from './basic/schema';
import { ComponentBuilder } from './common/component';
import { EntityBuilder } from './common/entity';

function getModels(mol, ctx) {
  var molecules = mol.molecules;
  var count = 0;

  for (var _i = 0, molecules_1 = molecules; _i < molecules_1.length; _i++) {
    var m = molecules_1[_i];
    count += m.count;
  }

  var type_symbols = new Array(count);
  var id = new Int32Array(count);
  var x = new Float32Array(count);
  var y = new Float32Array(count);
  var z = new Float32Array(count);
  var model_num = new Int32Array(count);
  var offset = 0;

  for (var i = 0; i < molecules.length; i++) {
    var m = molecules[i];

    for (var j = 0; j < m.count; j++) {
      type_symbols[offset] = m.type_symbol.value(j);
      x[offset] = m.x.value(j);
      y[offset] = m.y.value(j);
      z[offset] = m.z.value(j);
      id[offset] = j;
      model_num[offset] = i;
      offset++;
    }
  }

  var MOL = Column.ofConst('MOL', count, Column.Schema.str);
  var A = Column.ofConst('A', count, Column.Schema.str);
  var seq_id = Column.ofConst(1, count, Column.Schema.int);
  var type_symbol = Column.ofStringArray(type_symbols);
  var atom_site = Table.ofPartialColumns(BasicSchema.atom_site, {
    auth_asym_id: A,
    auth_atom_id: type_symbol,
    auth_comp_id: MOL,
    auth_seq_id: seq_id,
    Cartn_x: Column.ofFloatArray(x),
    Cartn_y: Column.ofFloatArray(y),
    Cartn_z: Column.ofFloatArray(z),
    id: Column.ofIntArray(id),
    label_asym_id: A,
    label_atom_id: type_symbol,
    label_comp_id: MOL,
    label_seq_id: seq_id,
    label_entity_id: Column.ofConst('1', count, Column.Schema.str),
    occupancy: Column.ofConst(1, count, Column.Schema.float),
    type_symbol: type_symbol,
    pdbx_PDB_model_num: Column.ofIntArray(model_num)
  }, count);
  var entityBuilder = new EntityBuilder();
  entityBuilder.setNames([['MOL', 'Unknown Entity']]);
  entityBuilder.getEntityId('MOL', 0
  /* Unknown */
  , 'A');
  var componentBuilder = new ComponentBuilder(seq_id, type_symbol);
  componentBuilder.setNames([['MOL', 'Unknown Molecule']]);
  componentBuilder.add('MOL', 0);
  var basics = createBasic({
    entity: entityBuilder.getEntityTable(),
    chem_comp: componentBuilder.getChemCompTable(),
    atom_site: atom_site
  });
  return createModels(basics, XyzFormat.create(mol), ctx);
} //


export { XyzFormat };
var XyzFormat;

(function (XyzFormat) {
  function is(x) {
    return (x === null || x === void 0 ? void 0 : x.kind) === 'xyz';
  }

  XyzFormat.is = is;

  function create(mol) {
    return {
      kind: 'xyz',
      name: 'xyz',
      data: mol
    };
  }

  XyzFormat.create = create;
})(XyzFormat || (XyzFormat = {}));

export function trajectoryFromXyz(mol) {
  return Task.create('Parse XYZ', function (ctx) {
    return getModels(mol, ctx);
  });
}